import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Trash2, Plus, FlaskConical, Loader2, Info, Rss } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = [
  ["major_city", "Major cities"], ["border", "Border updates"],
  ["rest_short", "Short rest stops"], ["rest_long", "Longer rest stops"],
  ["overnight", "Overnight stops"], ["resumed", "Trip resumed"],
  ["arrival", "Arrival"], ["completion", "Trip completion"],
];
const CAT_LABEL = Object.fromEntries(CATEGORIES);

const statusColor = (s) => ({
  sent: "bg-green-100 text-green-700", dry_run: "bg-blue-100 text-blue-700",
  retry: "bg-amber-100 text-amber-700", queued: "bg-charcoal-100 text-charcoal-600",
  failed: "bg-red-100 text-red-700", suppressed_quiet: "bg-purple-100 text-purple-700",
  portal_only: "bg-sand-200 text-charcoal-600", sms_dry_run: "bg-sand-200 text-charcoal-600",
  sms_sandbox: "bg-blue-100 text-blue-700",
}[s] || "bg-charcoal-100 text-charcoal-600");

const emptyForm = {
  email: "", name: "", phone: "", phone_country: "+1", role: "recipient",
  language: "en", categories: ["major_city", "border", "arrival", "completion"],
  channel: ["portal", "email"], consent: true,
  quiet: { enabled: false, start: "21:00", end: "07:00", tz: "America/Merida", bypass: true },
};

export default function TripNotifyPanel({ tripId }) {
  const [subs, setSubs] = useState([]);
  const [log, setLog] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [test, setTest] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/admin/trips/${tripId}/subscribers`).then((r) => setSubs(r.data)).catch(() => {});
    api.get(`/admin/trips/${tripId}/notifications`).then((r) => setLog(r.data)).catch(() => {});
    api.get(`/admin/trips/${tripId}/events`).then((r) => setEvents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tripId]);

  const toggleCat = (c) => setForm((f) => ({ ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c] }));
  const toggleCh = (c) => setForm((f) => ({ ...f, channel: f.channel.includes(c) ? f.channel.filter((x) => x !== c) : [...f.channel, c] }));

  const add = async () => {
    if (form.role !== "viewer" && !form.email.trim() && !form.phone.trim()) return toast.error("Enter an email (or phone for future SMS)");
    try { await api.post(`/admin/trips/${tripId}/subscribers`, form); toast.success("Recipient saved"); setForm(emptyForm); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delSub = async (id) => { try { await api.delete(`/admin/subscribers/${id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const sendTest = async (dry) => {
    if (!test.trim()) return toast.error("Enter a test recipient email");
    setBusy(true);
    try { const { data } = await api.post(`/admin/trips/${tripId}/notify-test`, { email: test, dry_run: dry });
      toast.success(dry ? "Dry-run OK (nothing sent)" : (data.ok ? "Test email sent" : "Send failed — check log")); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="notify-panel">
      <p className="text-xs text-charcoal-500 bg-sand-50 rounded-lg p-3">
        <Info size={13} className="inline mr-1 text-terracotta" />
        Routine city/stop alerts are sent <b>immediately</b> as GPS-detected events (not "visited/arrived") and don't wait for confirmation. Borders are sent as <b>possible crossings pending confirmation</b>. Emails go <b>server-side via Resend</b>; <b>SMS via Sent.dm</b> (US +1 / Mexico +52) runs in <b>sandbox/dry-run</b> until the Sent.dm key is added and go-live is approved. Sent.dm handles STOP/opt-outs and quiet hours. Messages never contain precise coordinates. Public posting is disabled.
      </p>

      {/* Add recipient */}
      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-3">
        <div className="font-medium text-charcoal-800 text-sm flex items-center gap-2"><Mail size={15} /> Add / update recipient</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="sub-email" className="border rounded-lg px-3 py-2 text-sm" placeholder="email@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="sub-name" className="border rounded-lg px-3 py-2 text-sm" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="flex gap-2">
            <select data-testid="sub-country" className="border rounded-lg px-2 text-sm" value={form.phone_country} onChange={(e) => setForm({ ...form, phone_country: e.target.value })}>
              <option value="+1">US +1</option><option value="+52">MX +52</option>
            </select>
            <input data-testid="sub-phone" className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="Phone (for future SMS)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <select data-testid="sub-role" className="border rounded-lg px-2 text-sm flex-1" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="admin">Admin (may confirm events)</option>
              <option value="recipient">Recipient (updates only)</option>
              <option value="viewer">Portal viewer</option>
            </select>
            <select data-testid="sub-lang" className="border rounded-lg px-2 text-sm" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
              <option value="en">English</option><option value="es">Español</option>
            </select>
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-charcoal-400 mb-1">Notification categories</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {CATEGORIES.map(([c, label]) => (
              <label key={c} className="flex items-center gap-1"><input data-testid={`cat-${c}`} type="checkbox" checked={form.categories.includes(c)} onChange={() => toggleCat(c)} /> {label}</label>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-xs items-center">
          <span className="text-charcoal-400">Channel:</span>
          {["portal", "email", "sms"].map((c) => <label key={c} className="flex items-center gap-1"><input data-testid={`ch-${c}`} type="checkbox" checked={form.channel.includes(c)} onChange={() => toggleCh(c)} /> {c === "sms" ? "SMS (Sent.dm)" : c}</label>)}
          <label className="flex items-center gap-2 ml-2"><input data-testid="sub-quiet" type="checkbox" checked={form.quiet.enabled} onChange={(e) => setForm({ ...form, quiet: { ...form.quiet, enabled: e.target.checked } })} /> Quiet hours</label>
          {form.quiet.enabled && (<>
            <input type="time" className="border rounded px-1 text-xs" value={form.quiet.start} onChange={(e) => setForm({ ...form, quiet: { ...form.quiet, start: e.target.value } })} />
            <span>to</span>
            <input type="time" className="border rounded px-1 text-xs" value={form.quiet.end} onChange={(e) => setForm({ ...form, quiet: { ...form.quiet, end: e.target.value } })} />
            <label className="flex items-center gap-1"><input type="checkbox" checked={form.quiet.bypass} onChange={(e) => setForm({ ...form, quiet: { ...form.quiet, bypass: e.target.checked } })} /> border/arrival bypass</label>
          </>)}
        </div>
        <button data-testid="sub-add-btn" onClick={add} className="bg-terracotta text-white rounded-full px-4 py-1.5 text-sm flex items-center gap-1"><Plus size={14} /> Save recipient</button>
      </div>

      {/* Recipient list */}
      <div className="rounded-xl border border-sand-200 bg-white p-4">
        <div className="font-medium text-charcoal-800 text-sm mb-2">Recipients ({subs.length})</div>
        <ul className="divide-y divide-sand-100">
          {subs.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`sub-${s.id}`}>
              <span className="font-medium text-charcoal-700">{s.email || s.phone || s.name}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{s.role}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">{s.language?.toUpperCase()}</span>
              {s.quiet?.enabled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">quiet {s.quiet.start}-{s.quiet.end}</span>}
              {(s.channel || []).includes("email") && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sand-100 text-charcoal-600">email</span>}
              {(s.channel || []).includes("sms") && s.phone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">SMS {s.phone_country}</span>}
              {s.unsubscribed && <span className="text-xs bg-red-100 text-red-700 px-2 rounded-full">unsubscribed</span>}
              <div className="flex flex-wrap gap-1 w-full mt-1">
                {(s.categories || []).map((c) => <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-sand-50 text-charcoal-500">{CAT_LABEL[c] || c}</span>)}
              </div>
              <button data-testid={`sub-del-${s.id}`} onClick={() => delSub(s.id)} className="ml-auto text-charcoal-400 hover:text-red-600"><Trash2 size={13} /></button>
            </li>
          ))}
          {subs.length === 0 && <li className="text-sm text-charcoal-500 py-2">No recipients yet.</li>}
        </ul>
      </div>

      {/* Test send */}
      <div className="rounded-xl border border-sand-200 bg-white p-4 space-y-2">
        <div className="font-medium text-charcoal-800 text-sm flex items-center gap-2"><FlaskConical size={15} /> Test send</div>
        <div className="flex flex-wrap gap-2">
          <input data-testid="test-email" className="border rounded-lg px-3 py-2 text-sm flex-1" placeholder="test recipient email" value={test} onChange={(e) => setTest(e.target.value)} />
          <button data-testid="test-dry-btn" onClick={() => sendTest(true)} disabled={busy} className="border rounded-full px-4 text-sm disabled:opacity-60">Dry-run</button>
          <button data-testid="test-send-btn" onClick={() => sendTest(false)} disabled={busy} className="bg-charcoal-700 text-white rounded-full px-4 text-sm flex items-center gap-1 disabled:opacity-60">{busy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Send real test</button>
        </div>
      </div>

      {/* Event feed */}
      <div className="rounded-xl border border-sand-200 bg-white p-4">
        <div className="font-medium text-charcoal-800 text-sm mb-2 flex items-center gap-2"><Rss size={14} /> Detected events feed</div>
        <div className="space-y-1 max-h-56 overflow-auto">
          {events.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-xs py-1 border-b border-sand-50" data-testid={`event-${e.id}`}>
              <span className="text-charcoal-700 flex-1 truncate">{e.title_en || e.title || e.city || e.event_type}</span>
              {e.delayed && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">delayed</span>}
              {e.needs_confirmation && <span className="px-2 py-0.5 rounded-full bg-sand-200 text-charcoal-600">confirm?</span>}
              <span className="px-2 py-0.5 rounded-full bg-charcoal-100 text-charcoal-600">{e.event_type}</span>
            </div>
          ))}
          {events.length === 0 && <div className="text-sm text-charcoal-500">No events yet.</div>}
        </div>
      </div>

      {/* Delivery log */}
      <div className="rounded-xl border border-sand-200 bg-white p-4">
        <div className="font-medium text-charcoal-800 text-sm mb-2">Delivery log</div>
        <div className="space-y-1 max-h-64 overflow-auto">
          {log.map((n) => (
            <div key={n.id} className="flex items-center gap-2 text-xs py-1 border-b border-sand-50" data-testid={`notif-${n.id}`}>
              <span className="text-charcoal-600 flex-1 truncate">{n.email} · {n.event_type}</span>
              <span className="text-charcoal-400">×{n.attempts}</span>
              {n.last_error && <span className="text-red-500 truncate max-w-[120px]">{n.last_error}</span>}
              <span className={`px-2 py-0.5 rounded-full ${statusColor(n.status)}`}>{n.status}</span>
            </div>
          ))}
          {log.length === 0 && <div className="text-sm text-charcoal-500">No notifications yet.</div>}
        </div>
      </div>
    </div>
  );
}
